import React, { useCallback, useMemo, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { parse, stringify } from 'qs'
import { Box, Tooltip } from '@mui/material'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'

import { TableUrlPagination } from '../../../../ui'
import { useCombos } from '../../../combos'
import { usePORequests } from '../../hooks'
import { poRequestsTableDataPreparation } from '../../logics'
import { PO_REQ_TABLE_COLUMNS, DEFAULT_QUERY } from './po-requests-table.constants'
import { checkEqualArrays } from '../../../../core/utils/arrayUtils'
import { PO_MASSIVE_ACTIONS_STATES } from '../../constants'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const PORequestsTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const combos = useCombos([])
  const {
    data,
    pagination: { total_registros: totalEntries },
    loading,
    search,
    tableSelection,
    onClickTableRow,
    setSelectedTableRows,
    getTableData,
    msLoaded,
  } = usePORequests()

  useEffect(() => {
    const searchString = location.search.split('?')[1]
    if (searchString) {
      const urlSearchParams = parse(searchString)
      const searchParams = {
        ...DEFAULT_QUERY,
        ...urlSearchParams,
      }
      getTableData(searchParams)
    } else {
      navigate(`?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, getTableData, navigate])

  const isColumnSelectionDisabled = (rowData) => {
    if (!PO_MASSIVE_ACTIONS_STATES.includes(rowData.estado)) {
      return true
    }

    if (tableSelection.length === 0) {
      return false
    }
    return (
      tableSelection[0].empresa_ca !== rowData.empresa_ca ||
      tableSelection[0].estado !== rowData.estado ||
      tableSelection[0].sociedad !== rowData.sociedad ||
      !checkEqualArrays(tableSelection[0].mercados, rowData.mercados)
    )
  }

  const handleSelectRows = useCallback(
    (rows) => {
      setSelectedTableRows(rows)
    },
    [setSelectedTableRows]
  )

  const formattedColumns = useMemo(
    () =>
      PO_REQ_TABLE_COLUMNS.map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.poRequests.table.columns.${column.field}.title` }),
          size: column.width,
        }

        if (column.field === 'cambios_pendientes') {
          return {
            ...baseColumn,
            header: '',
            Header: () => (
              <Tooltip
                title={formatMessage({
                  id: `pages.poRequests.table.columns.${column.field}.title`,
                })}
              >
                <NotificationsNoneIcon sx={{ fontSize: 18 }} />
              </Tooltip>
            ),
            Cell: ({ row }) =>
              row.original.cambios_pendientes > 0 ? (
                <Box display="flex" alignItems="center" justifyContent="center">
                  <NotificationsActiveIcon color="secondary" sx={{ fontSize: 18 }} />
                </Box>
              ) : null,
            muiTableBodyCellProps: { align: 'center' },
          }
        }

        if (!column.hasOwnProperty('multiple') && column.comboId && combos[column.comboId]?.data) {
          const lookup = getLookup(combos[column.comboId].data)
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              return <span>{lookup[value] ?? value ?? '--'}</span>
            },
          }
        }

        return {
          ...baseColumn,
          Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
        }
      }),
    [combos, formatMessage]
  )

  const processedData = useMemo(
    () => poRequestsTableDataPreparation({ data, combos, intl }),
    [data, combos, intl]
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: true,
      showSelectAllCheckbox: false,
      tableLayout: 'fixed',
      draggable: false,
    }),
    []
  )

  const handleRowClick = useCallback(
    (_, row) => {
      onClickTableRow(row.id_peticion)
    },
    [onClickTableRow]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={handleRowClick}
      onSelectionChange={handleSelectRows}
      enableRowSelection={(row) => !isColumnSelectionDisabled(row.original)}
      resetSelectionTrigger={msLoaded}
    />
  )
}
